/*
This file is located in the main branch
Last time the file was updated (UTC): [11.12.2024 08:50:34]
Permissions to update outside the main branch: [true]
*/

import {
    DropDown,
    DynamicListRowWrapperDefault,
    ImageUpload,
    requiredRule,
    useErrorState,
    useToastContext,
    useUpdateEffect,
    ValidatedDropDownList,
    ValidatedDynamicList,
    ValidatedInput
} from '@finbeat/react-toolkit';
import { getQuestionaryAll } from 'axios/questionaryApi';
import { getTradeOrgsDropDownList } from 'axios/tradeOrgsApi';
import useApi from 'hooks/useApi';
import { TradeOrgIntegrator } from 'models/api/tradeOrg';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Col, FormGroup, Row } from 'reactstrap';
import { TradeOrgAddForm } from './TradeOrgChangeModal';
import { TradeOrgIntegratorsForm, TradeOrgIntegratorsFormWrapper } from './TradeOrgIntegratorsForm';

type TabMainProps = {
    productCategories: DropDown[];
    integrationTypes: DropDown[];
    notifySellers: DropDown[];
    orderStatuses: DropDown[];
    dictsLoading: boolean;
    formHasRole: boolean;
    showAdditionalFields: boolean;
};

const TradeOrgChangeModalTabMain = ({
    productCategories,
    integrationTypes,
    notifySellers,
    orderStatuses,
    dictsLoading,
    formHasRole,
    showAdditionalFields,
}: TabMainProps) => {
    const api = useApi();
    const { t } = useTranslation();
    const { handleRequestError } = useErrorState();
    const { addToast } = useToastContext();
    const { cultures } = useGeneralSettingsContext();
    const { watch, setValue, getValues } = useFormContext<TradeOrgAddForm>();

    const logoFilePath = getValues('logoFilePath');
    const backgroundFilePath = getValues('backgroundFilePath');

    const [logoFileValid, setLogoFileValid] = useState(true);
    const [backgroundFileValid, setBackgroundFileValid] = useState(true);

    const [clientQuestionaries, setClientQuestionaries] = useState<DropDown[]>([]);

    const { isFetching: questionariesFetching } = useQuery(
        ['questionaries'],
        () => getQuestionaryAll(api),
        {
            onError: handleRequestError,
            onSuccess: (data) => {
                setClientQuestionaries(
                    data.map(({ id, name }) => ({ id: id ?? null, name: name }))
                );
            },
        }
    );

    const { isFetching: tradeOrgsFetching, data: tradeOrgs } = useQuery(
        ['tradeOrgsList'],
        () => getTradeOrgsDropDownList(api),
        {
            onError: handleRequestError,
            refetchOnWindowFocus: false,
        }
    );

    const chooseIntegratorsFlag = watch('integrators.chooseIntegratorsFlag');

    const validateImageAndSetState = (
        file: File | null,
        fieldName: keyof TradeOrgAddForm,
        setValid: (valid: boolean) => void
    ) => {
        const maxSize = 512 * 1024;
        const isValid = file?.type?.startsWith('image/') && file?.size <= maxSize;
        if (isValid) {
            setValue(fieldName, file);
            setValid(true);
        } else {
            setValue(fieldName, null);
            setValid(false);
            addToast({
                type: 'warning',
                title: t('BackOfficeTradeOrgImageError',
                    {
                        maxImageSize: (maxSize / 1024).toString()
                    })
            });
        }
    };


    useUpdateEffect(() => {
        setValue('integrators.list', [{ tradeOrgId: null }]);
    }, [chooseIntegratorsFlag]);

    return (
        <>
            <ValidatedInput<TradeOrgAddForm>
                id="isActiveCheckbox"
                className="my-3"
                type="checkbox"
                name="isActive"
                label={t('BackOfficeIsActive')}
                disabled={!formHasRole}
            />
            <ValidatedInput<TradeOrgAddForm>
                id="isLandingShowsCheckbox"
                className="my-3"
                type="checkbox"
                name="isLandingShows"
                label={t('BackOfficeIsLandingShows')}
                disabled={!formHasRole}
            />
            <FormGroup>
                <Row>
                    <Col xs={6}>
                        <h5><Trans i18nKey="BackOfficeLogo" /></h5>
                        <ImageUpload
                            canUpload={false}
                            showPreview={logoFileValid}
                            alt={t('BackOfficeLogoNotFound')}
                            onChange={
                                (f) => validateImageAndSetState(f, 'logoFile', setLogoFileValid)
                            }
                            maxHeight={230}
                            filePath={logoFilePath ?? ''}
                        />
                    </Col>
                    <Col xs={6}>
                        <h5><Trans i18nKey="BackOfficeBackground" /></h5>
                        <ImageUpload
                            canUpload={false}
                            alt={t('BackOfficeBackgroundNotFound')}
                            showPreview={backgroundFileValid}
                            onChange={
                                (f) => validateImageAndSetState(f, 'backgroundFile', setBackgroundFileValid)
                            }
                            maxHeight={230}
                            filePath={backgroundFilePath ?? ''}
                        />
                    </Col>
                </Row>
            </FormGroup>
            <Row>
                <Col>
                    <ValidatedInput<TradeOrgAddForm>
                        type="text"
                        name="brandName"
                        label={t('BackOfficeBrandName')}
                        rules={{ required: requiredRule }}
                        disabled={!formHasRole}
                    />
                </Col>
                <Col>
                    <ValidatedInput<TradeOrgAddForm>
                        type="text"
                        name="legalEntityName"
                        label={t('BackOfficeLegalEntityName')}
                        disabled={!formHasRole}
                    />
                </Col>
            </Row>
            <ValidatedInput<TradeOrgAddForm>
                type="url"
                name="url"
                label={t('BackOfficeTradeOrgUrl')}
                disabled={!formHasRole}
            />
            <ValidatedDropDownList<TradeOrgAddForm>
                name="productCategory"
                label={t('BackOfficeProductCategory')}
                items={productCategories}
                isSearchable
                isLoading={dictsLoading}
                disabled={!formHasRole}
                rules={{
                    required: requiredRule
                }}
            />
            {showAdditionalFields && (
                <>
                    <ValidatedDropDownList<TradeOrgAddForm>
                        name="questionaryId"
                        label={t('BackOfficeClientQuestionary')}
                        items={clientQuestionaries}
                        isSearchable
                        isLoading={questionariesFetching}
                        disabled={!formHasRole}
                    />
                    <Row>
                        <Col>
                            <ValidatedDropDownList<TradeOrgAddForm>
                                name="integrationType"
                                label={t('BackOfficeIntegrationType')}
                                items={integrationTypes}
                                isLoading={dictsLoading}
                                disabled={!formHasRole}
                            />
                        </Col>
                        <Col>
                            <ValidatedDropDownList<TradeOrgAddForm>
                                name="notifyConfirmedOrder"
                                label={t('BackOfficeNotifySeller')}
                                items={notifySellers}
                                isLoading={dictsLoading}
                                disabled={!formHasRole}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <ValidatedDropDownList<TradeOrgAddForm>
                                name="loanStatus"
                                label={t('BackOfficeLoanStatus')}
                                items={orderStatuses}
                                isLoading={dictsLoading}
                                disabled={!formHasRole}
                            />
                        </Col>
                        <Col>
                            <ValidatedDropDownList<TradeOrgAddForm>
                                name="paymentStatus"
                                label={t('BackOfficePaymentStatus')}
                                items={orderStatuses}
                                isLoading={dictsLoading}
                                disabled={!formHasRole}
                            />
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col>
                            <ValidatedInput<TradeOrgAddForm>
                                id="checkShip"
                                type="checkbox"
                                name="checkShip"
                                label={t('BackOfficeCheckShip')}
                                disabled={!formHasRole}
                            />
                        </Col>
                        <Col>
                            <ValidatedInput<TradeOrgAddForm>
                                id="askShipType"
                                type="checkbox"
                                name="askShipType"
                                label={t('BackOfficeAskShipType')}
                                disabled={!formHasRole}
                            />
                        </Col>
                    </Row>
                    <ValidatedInput<TradeOrgAddForm>
                        id="chooseIntegratorsFlag"
                        className="my-3"
                        type="checkbox"
                        name="integrators.chooseIntegratorsFlag"
                        label={t('BackOfficeChooseIntegrators')}
                        disabled={!formHasRole}
                    />
                    {chooseIntegratorsFlag && (
                        <ValidatedDynamicList<TradeOrgAddForm, TradeOrgIntegrator>
                            formComponent={(props) => (
                                <TradeOrgIntegratorsForm
                                    {...props}
                                    tradeOrgs={tradeOrgs || []}
                                    tradeOrgsFetching={tradeOrgsFetching}
                                    formHasRole={formHasRole}
                                />
                            )}
                            wrapper={TradeOrgIntegratorsFormWrapper}
                            name="integrators.list"
                            emptyRecord={{ tradeOrgId: null }}
                            prepend={false}
                            compareFunc={(a, b) => a?.tradeOrgId === b?.tradeOrgId}
                            rowWrapper={(props) => (
                                <DynamicListRowWrapperDefault
                                    {...props}
                                    disabled={props.disabled || props.idx === 0}
                                />
                            )}
                        />
                    )}
                </>
            )}
            {
                cultures?.map((x) => {
                    return (
                        <ValidatedInput<TradeOrgAddForm>
                            key={x.code}
                            name={`descriptions.${x.code}`}
                            label={`${t('BackOfficeDescription')} ${x.name}`}
                            rules={{ required: requiredRule }}
                        />
                    );
                })
            }
        </>
    );
};

export default TradeOrgChangeModalTabMain;
